import { ShieldCheck } from "lucide-react";
import { FormEvent, useState } from "react";
import { errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button, Field, Input } from "../components/ui";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="hidden lg:flex flex-col justify-between bg-[hsl(var(--sidebar))] text-slate-200 p-12 relative overflow-hidden">
        <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute -left-20 bottom-0 h-72 w-72 rounded-full bg-amber-500/10 blur-3xl" />
        <div className="flex items-center gap-3 relative">
          <div className="h-10 w-10 rounded-lg bg-primary grid place-items-center font-display font-extrabold text-primary-foreground text-lg">Y</div>
          <div>
            <p className="font-display font-bold text-white">Yutaka Edu-Finance</p>
            <p className="text-xs uppercase tracking-widest text-slate-400">Koperasi Pendidikan</p>
          </div>
        </div>
        <div className="relative max-w-lg">
          <h1 className="font-display text-4xl xl:text-5xl font-bold text-white leading-tight">Kontrol outstanding, status bayar, dan kolektibilitas dalam satu layar.</h1>
          <p className="mt-5 text-slate-400 leading-relaxed">1 siswa = 1 pinjaman aktif. NIK resmi sebagai identitas, transaksi final tidak dihapus langsung, setiap koreksi lewat approval — siap diaudit.</p>
          <div className="mt-8 flex flex-wrap gap-2 text-xs font-semibold">
            <span className="rounded-full bg-red-500/20 text-red-300 px-3 py-1">Open</span>
            <span className="rounded-full bg-amber-500/20 text-amber-300 px-3 py-1">Partial Paid</span>
            <span className="rounded-full bg-emerald-500/20 text-emerald-300 px-3 py-1">Paid</span>
            <span className="rounded-full bg-blue-500/20 text-blue-300 px-3 py-1">Lunas</span>
          </div>
        </div>
        <p className="text-xs text-slate-500 relative">© {new Date().getFullYear()} Yutaka Edu-Finance · Internal use</p>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm rise" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-lg bg-primary grid place-items-center font-display font-extrabold text-primary-foreground text-lg">Y</div>
            <p className="font-display font-bold text-lg">Yutaka Edu-Finance</p>
          </div>
          <h2 className="font-display text-2xl font-bold">Masuk</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-6">Gunakan email & password yang ditetapkan Super Admin.</p>
          <div className="space-y-4">
            <Field label="Email">
              <Input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nama@koperasi.id" autoComplete="username" />
            </Field>
            <Field label="Password">
              <Input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" />
            </Field>
            {error && (
              <p data-testid="login-error" className="rounded-md border border-red-300 bg-red-50 dark:bg-red-950/40 dark:border-red-800 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                {error}
              </p>
            )}
            <Button data-testid="login-submit-button" type="submit" className="w-full" disabled={busy}>
              <ShieldCheck className="h-4 w-4" /> {busy ? "Memverifikasi…" : "Masuk ke sistem"}
            </Button>
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-wider text-muted-foreground"><span className="h-px flex-1 bg-border" />atau<span className="h-px flex-1 bg-border" /></div>
            <Button data-testid="login-google-button" type="button" variant="outline" className="w-full" onClick={loginWithGoogle}>
              <svg className="h-4 w-4" viewBox="0 0 48 48" aria-hidden><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.8 6C12.3 13.6 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.4 5.7c4.3-4 7.2-9.9 7.2-17.4z"/><path fill="#FBBC05" d="M10.4 28.7A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.8-4.7l-7.8-6A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.8-6z"/><path fill="#34A853" d="M24 48c6.2 0 11.6-2 15.4-5.6l-7.4-5.7c-2 1.4-4.7 2.3-8 2.3-6.3 0-11.7-4.1-13.6-9.8l-7.8 6C6.5 42.6 14.6 48 24 48z"/></svg>
              Masuk dengan Google
            </Button>
            <p className="text-[11px] text-muted-foreground text-center">Login Google hanya untuk email terdaftar yang telah diizinkan Super Admin.</p>
          </div>
        </form>
        <p className="mt-4 text-center text-xs text-muted-foreground">Siswa? <a href="/instal" data-testid="login-install-link" className="font-semibold text-primary underline-offset-2 hover:underline">Lihat QR & cara instal aplikasi di HP</a></p>
      </div>
    </div>
  );
}
